import React, { createContext, useState, useEffect, useContext, useMemo } from 'react'
import { Banner, fetchActiveBanners, fetchStandings, TeamStanding } from '../../Api'
import { filterStandings, SEASON_SEGMENT_NAMES } from '../../Utils'
import { SeasonContext } from '../SeasonContext'
import { Season } from '../SeasonContext/SeasonContext'
import { useStorageContext } from '../StorageContext'
import { STORAGE_NAMES } from '../../Constant'
import { SEASON_SELECTOR_TYPES } from '../../Constant/season'

type StandingsContextType = {
  standings: TeamStanding[]
  banner: Banner
  loading: boolean
  getStandings: (seasonSelect: Season) => Promise<void>
}

export const StandingsContext = createContext<StandingsContextType | null>(null)

export const StandingsProvider: React.FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const selectorType = SEASON_SELECTOR_TYPES.STANDINGS
  const { seasonSelect } = useContext(SeasonContext)!
  const { isStorageReady, fetchAndCache } = useStorageContext()
  const [standings, setStandings] = useState<TeamStanding[]>([])
  const [banner, setBanner] = useState<Banner>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [yearObj, setYearObj] = useState<Season | null>()

  useEffect(() => {
    if (isStorageReady) {
      fetchData()
    }
  }, [isStorageReady])

  useEffect(() => {
    updateStandings()
  }, [seasonSelect[selectorType]])

  const fetchData = async (): Promise<any> => {
    await Promise.all([fetchBanners(), getStandings(seasonSelect[selectorType])])
  }

  const updateStandings = async () => {
    if (
      seasonSelect[selectorType].year !== yearObj?.year ||
      seasonSelect[selectorType].seasonType !== yearObj?.seasonType
    ) {
      setLoading(true)
      // Refetch & update standings
      setYearObj(seasonSelect[selectorType])
      await getStandings(seasonSelect[selectorType])
    }
  }

  const getStandings = async (season: Season) => {
    const champSeriesSelected = season.seasonType === SEASON_SEGMENT_NAMES.champseries
    const fetcher = async (): Promise<TeamStanding[]> => {
      const teams = await fetchStandings(season.year, champSeriesSelected)
      return teams || []
    }

    const onUpdate = (teams: TeamStanding[]) => {
      if (teams && teams.length > 0) {
        const filteredTeams = filterStandings(teams, season)
        setStandings(filteredTeams)
      }
      setLoading(false)
    }

    try {
      await fetchAndCache<TeamStanding[]>(
        `${STORAGE_NAMES.standings}_${season.year}_${season.seasonType}`,
        fetcher,
        onUpdate
      )
    } catch (err: any) {
      setLoading(false)
      return err
    }
  }

  const fetchBanners = async () => {
    try {
      const bannerRes = await fetchActiveBanners()
      if (!bannerRes || bannerRes.length < 1) return
      const standingsBanner = bannerRes.find((banner) =>
        banner?.location.includes('standings')
      )
      setBanner(standingsBanner ?? null)
    } catch (err) {
      console.log(err)
    }
  }
  const contextValue = useMemo(
    () => ({
      standings: standings,
      banner: banner,
      loading: loading,
      getStandings: getStandings,
    }),
    [standings, banner, loading]
  )

  return (
    <StandingsContext.Provider value={contextValue}>
      {children}
    </StandingsContext.Provider>
  )
}

export const useStandingsContext = (): StandingsContextType => {
  try {
    const context = useContext(StandingsContext)
    if (!context) {
      throw new Error(
        'useStandingsContext must be used within an StandingsProvider. Make sure your component is wrapped with StandingsProvider.'
      )
    }
    return context
  } catch (error) {
    console.error('Error in useStandingsContext:', error)
    console.error('Current StandingsContext:', StandingsContext)
    throw error
  }
}
